import { formatCurrency } from "@/shared/currency";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseService from "@/shared/api/expenseService";
import {
    FetchState,
    ExpenseReimbursedSummaryState,
    ExpenseReimbursedSummaryResponse
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";
import { useThemeStore } from "@/shared/store/theme";

export const initialState: ExpenseReimbursedSummaryState = {
    fetchState: FetchState.Uninitialized,
    yearlyDataPoints: [],
    monthlyDataPoints: [],
    graphStartDate: ""
};


export const useExpenseReimbursedSummaryStore = defineStore("expenseReimbursedSummary", {
    state: (): ExpenseReimbursedSummaryState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: s => s.fetchState === FetchState.Loaded,
        yearSummary: s => s.yearlyDataPoints,
        monthlySummaryGraphData () {
            if (!this.isLoaded) {
                return [];
            }

            const dataPoints = this.monthlyDataPoints;

            const data: any[] = [ [
                "Date",
                "Reimbursed",
                { role: "style" },
                {
                    type: "string", role: "tooltip", p: {
                        html: true
                    }
                }
            ] ];

            const themeStore = useThemeStore();
            const secondaryColor = themeStore.secondaryColor;
            const barColor = secondaryColor;

            dataPoints.forEach(dataPoint => {
                const tooltipHtml = `<div style="padding:10px 15px; min-width: 120px;">
                            <div style="font-weight: bold; margin-bottom: 3px;">
                              ${moment(dataPoint.month).format("MMM YYYY")}
                            </div>
                            <div style="color: ${barColor}; font-weight: bold;">
                              ${formatCurrency(dataPoint.amount)}
                            </div>
                          </div>`;

                data.push([
                    moment(dataPoint.month).format("MMM"),
                    dataPoint.amount,
                    `color: ${barColor}; opacity: 0.5;`,
                    tooltipHtml ]);
            });

            return data;
        },
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setGraphData (response: { serverResponse: ExpenseReimbursedSummaryResponse }) {
            const sourceYearly = response.serverResponse.yearly;
            const sourceMonthly = response.serverResponse.monthly;

            this.yearlyDataPoints = _.sortBy(sourceYearly, "year").reverse().slice(0, 2);
            this.monthlyDataPoints = sourceMonthly.map(({ amount, month }) => ({ amount, month: moment(month) }));
            this.graphStartDate = response.serverResponse.graphStartDate;
            this.fetchState = FetchState.Loaded;
        },
        load (args: { startDate: string, endDate: string }) {
            if (this.fetchState !== FetchState.Uninitialized) return;
            this.setFetchState(FetchState.Loading);
            return expenseService.getReimbursementMonthlySummary(args.startDate, args.endDate)
                .then(response => {
                    this.setGraphData({ serverResponse: response.data });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        }
    }
});
